import React, { useState } from "react";
import PublicLayout from "../layouts/PublicLayout";
import SEOHead from "../components/SEOHead";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowRight, Building, CheckCircle, Sparkles, Award, Globe } from "lucide-react";
import { COUNTRIES } from "../data/countries";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const [formData, setFormData] = useState({
    organization_name: '',
    industry_sector: '',
    organization_size: '',
    country_region: '',
    contact_name: '',
    email: '',
    phone: '',
    desired_tier: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/organization-signup`, formData);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEOHead 
        title="Get Certified - Sign Up"
        description="Start your organization's CNESS certification journey. Join 500+ certified conscious businesses worldwide."
        url="/signup"
      />

      <>
        {/* Fresh Hero - clean, on-brand, no background image */}
        <section className="py-16 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="signup-hero">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm font-medium text-[#1E3A5F]">Certification Sign-up</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
                Start Your <span className="text-[#FDB813]">Certification Journey</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Join 500+ organisations across 30+ countries proving their commitment to conscious business
                through credible, independent certification.
              </p>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-600">
                <span className="flex items-center"><Award className="w-4 h-4 mr-1.5 text-[#FDB813]" /> 3 tiers</span>
                <span className="flex items-center"><Globe className="w-4 h-4 mr-1.5 text-[#FDB813]" /> Global recognition</span>
                <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1.5 text-green-600" /> 30-day average</span>
              </div>
            </div>
          </div>
        </section>

        {/* Signup Form Section */}
        <section className="py-16 bg-[#F8FAFC]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              
              {/* Organization Signup */}
              <Card className="shadow-2xl">
                <CardContent className="p-12">
                  {submitSuccess ? (
                    <div className="text-center">
                      <CheckCircle className="w-16 h-16 mx-auto mb-6 text-green-500" />
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        Application Submitted Successfully!
                      </h2>
                      <p className="text-gray-600 mb-8">
                        Thank you for your interest in CNESS certification. Our team will review your application and contact you within 24 hours.
                      </p>
                      <a href="/">
                        <Button className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                          Return to Home
                        </Button>
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-8">
                        <Building className="w-16 h-16 mx-auto mb-6 text-amber-600" />
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                          Organization Registration
                        </h2>
                        <p className="text-gray-600">
                          Complete your organization's profile to begin the certification process
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Organization Name *
                            </label>
                            <input 
                              type="text" 
                              name="organization_name"
                              value={formData.organization_name}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              placeholder="Your organization name"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Industry/Sector *
                            </label>
                            <select 
                              name="industry_sector"
                              value={formData.industry_sector}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" 
                              required
                            >
                              <option value="">Select your industry</option>
                              <option value="Technology">Technology</option>
                              <option value="Healthcare">Healthcare</option>
                              <option value="Manufacturing">Manufacturing</option>
                              <option value="Financial Services">Financial Services</option>
                              <option value="Retail">Retail</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Organization Size *
                            </label>
                            <select 
                              name="organization_size"
                              value={formData.organization_size}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" 
                              required
                            >
                              <option value="">Select organization size</option>
                              <option value="Micro (1-10 employees)">Micro (1-10 employees)</option>
                              <option value="Small (11-50 employees)">Small (11-50 employees)</option>
                              <option value="Medium (51-250 employees)">Medium (51-250 employees)</option>
                              <option value="Large (251-500 employees)">Large (251-500 employees)</option>
                              <option value="Enterprise (500+ employees)">Enterprise (500+ employees)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Country/Region *
                            </label>
                            <select 
                              name="country_region"
                              value={formData.country_region}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" 
                              required
                            >
                              <option value="">Select your country</option>
                              {COUNTRIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Contact Name *
                          </label>
                          <input 
                            type="text" 
                            name="contact_name"
                            value={formData.contact_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Organization admin name"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address *
                            </label>
                            <input 
                              type="email" 
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              placeholder="admin@yourorg.com"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number
                            </label>
                            <input 
                              type="tel" 
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              placeholder="+1 (555) 123-4567"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Desired Certification Tier
                          </label>
                          <select 
                            name="desired_tier"
                            value={formData.desired_tier}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          >
                            <option value="">Not sure yet</option>
                            <option value="Aspiring (Entry)">Aspiring (Entry)</option>
                            <option value="Inspired (Advanced)">Inspired (Advanced)</option>
                            <option value="Luminary (Leadership)">Luminary (Leadership)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tell us about your organization's conscious business journey
                          </label>
                          <textarea 
                            rows="4" 
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Share your current sustainability initiatives, values, and why you're interested in CNESS certification..."
                          ></textarea>
                        </div>

                        <div className="flex items-start">
                          <input 
                            type="checkbox" 
                            className="mt-1 mr-3 h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                            required
                          />
                          <label className="text-sm text-gray-600">
                            I agree to the <a href="/terms" className="text-amber-600 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-amber-600 hover:underline">Privacy Policy</a>. I understand that CNESS will contact me regarding certification opportunities.
                          </label>
                        </div>

                        <div className="text-center">
                          <Button 
                            type="submit"
                            disabled={isSubmitting}
                            size="lg"
                            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-12 py-4 text-lg font-semibold"
                          >
                            {isSubmitting ? 'Submitting...' : 'Begin Certification Process'}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                          <p className="text-sm text-gray-500 mt-4">
                            Free eligibility assessment • No commitment required • Response within 24 hours
                          </p>
                        </div>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </>
    </PublicLayout>
  );
};

export default SignupPage;